import { Injectable } from "@angular/core";
import { catchError, map, tap } from "rxjs/operators";

import { User, UserPreferences, UserStats, profileURL } from "./user.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import {
  CommonPaginationResponse,
  CommonResponse,
  PaginateResponse,
  processNormal,
  processPagination,
  VoidResponse,
} from "../common/response.model";
import { ErrorHandlingService } from "../common/error-handling.service";
import { Account, AccountUserType } from "../accounts/account.model";
import { Observable } from "rxjs";
import {
  getFullPhoneNumber,
  getPhoneNumberCountry,
  PhoneNumberModel,
} from "../../helpers/phone_number.helper";
import { PrescriptionDownload } from "../prescriptions/prescription.model";

@Injectable()
export class UserService {
  constructor(
    private http: HttpClient,
    private errorHandling: ErrorHandlingService
  ) {}

  login(user: string, password: string): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "login", {
        user,
        password,
      })
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  registerPatient(login_type: string, user: User): Observable<User> {
    const bodyParams = {
      name: user.name,
      last_name: user.last_name,
      email: user.email,
      phone_number: getFullPhoneNumber(user.phone_number_ui),
      phone_number_country: getPhoneNumberCountry(user.phone_number_ui),
      password: user.password,
      identity_document: user.identity_document,
      password_confirmation: user.password_confirmation,
      user_type: "individual",
    };

    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "register", bodyParams)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  activePatientBytoken(
    login_type: string,
    user: User,
    token: string
  ): Observable<User> {
    const bodyParams = {
      name: user.name,
      last_name: user.last_name,
      email: user.email,
      phone_number: getFullPhoneNumber(user.phone_number_ui),
      phone_number_country: getPhoneNumberCountry(user.phone_number_ui),
      password: user.password,
      password_confirmation: user.password_confirmation,
      identity_document: user.identity_document,
      user_type: "individual",
      token: token,
    };

    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "register/active-patient-by-token",
        bodyParams
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  resetPasswordRequest(type: string, data: User): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "reset-password", data)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  sendTokenVerify(type: string): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "user/validation/send-code/" + type,
        type
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }
  sendValidadTokenVerify(type: string, code: string): Observable<User> {
    const bodyParams = {
      token: code,
    };
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "user/validation/validate-code/" + type,
        bodyParams
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  acceptedInvitationMedical(token: string): Observable<User> {
    const bodyParams = {
      token: token,
    };
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "user/validation/accepted-invitation-medical",
        bodyParams
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  sendTokenMedical(token: string): Observable<Account> {
    const bodyParams = {
      token: token,
    };
    return this.http
      .post<CommonResponse<Account>>(
        environment.apiUrl + "user/validation/validate-invitation-medical",
        bodyParams
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  uploadFile(data: any): Observable<profileURL> {
    return this.http
      .post<CommonResponse<profileURL>>(
        environment.apiUrl + "user/upload/profile-photo",
        data
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  resetPasswordTokenValidity(token: string): Observable<User> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<User>>(environment.apiUrl + "reset-password/" + token)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  resetPasswordWithToken(token: string, data: User): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "reset-password/" + token + "/recover",
        {
          password: data.password,
          password_confirmation: data.password_confirmation,
        }
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  changePwd(data): Observable<VoidResponse> {
    return this.http
      .post<CommonResponse<VoidResponse>>(
        environment.apiUrl + "user/change-password",
        {
          password: data.actual_pwd,
          new_password: data.new_pwd,
          new_password_confirmation: data.new_pwd_confirm,
        }
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  confirmInvitationTokenValidity(token: string): Observable<User> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<User>>(environment.apiUrl + "invitation/" + token)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  getConsultationPdf(
    token: string,
    consultation: string,
    type: string
  ): Observable<PrescriptionDownload> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<PrescriptionDownload>>(
        environment.apiUrl +
          "show-consultation/" +
          consultation +
          "/download/" +
          type +
          "/" +
          token
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  confirmAccount(token: string): Observable<User> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<User>>(
        environment.apiUrl + "confirm-account/" + token
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  confirmInvitation(token: string, user: User): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "invitation/" + token + "/confirm",
        {
          password: user.password,
          password_confirmation: user.password_confirmation,
        }
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  logout(): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "logout", [])
      .pipe(map((resp) => processNormal(resp)));
  }

  /**
   * Obtener el listado de todos los usuarios
   *
   * @return Observable<User[]>
   */
  public listAll(): Observable<User[]> {
    return this.http
      .get<CommonResponse<User[]>>(environment.apiUrl + "users", {
        params: new HttpParams().append("no-paginate", "1"),
      })
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Listar todos los usuarios paginados
   *
   */
  listPaginated(
    page,
    per_page = 15,
    filter: string = ""
  ): Observable<PaginateResponse<User[]>> {
    let params = new HttpParams()
      .append("paginate_per_page", per_page.toString())
      .append("page", page);
    if (filter !== "") {
      params = params.append("filter", filter);
    }

    return this.http
      .get<CommonPaginationResponse<User[]>>(environment.apiUrl + "users", {
        params,
      })
      .pipe(
        map((resp) => processPagination(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Ver datos del usuario
   *
   */
  show(id: string): Observable<User> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<User>>(environment.apiUrl + "users/" + id)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Ver datos de perfil publico del usuario
   *
   */
  showPublicProfile(id: string): Observable<User> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<User>>(
        environment.apiUrl + "users/" + id + "/profile?public"
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Ver datos de perfil publico del usuario
   *
   */
  showPublicStats(id: string): Observable<UserStats> {
    const bodyParams = {};

    return this.http
      .get<CommonResponse<UserStats>>(
        environment.apiUrl + "users/" + id + "/profile/stats?public"
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Agregar el usuario
   *
   */
  add(
    type: string,
    user: User,
    account_user_type: string,
    account: string
  ): Observable<User> {
    const body = {
      name: user.name,
      last_name: user.last_name,
      email: user.email,
      phone_number: getFullPhoneNumber(user.phone_number_ui),
      phone_number_country: getPhoneNumberCountry(user.phone_number_ui),
      account_user_type,
      account,
    };

    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "users", body)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Editar el usuario
   *
   */
  edit(
    id: string,
    name: string,
    account_user_type: string,
    account: string = ""
  ): Observable<User> {
    const postBody = {
      name,
      account_user_type,
    };

    if (account !== "") {
      postBody["account"] = account;
    }

    return this.http
      .put<CommonResponse<User>>(environment.apiUrl + "users/" + id, postBody)
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  /**
   * Eliminar al usuario
   */
  delete(id: string): Observable<VoidResponse[]> {
    return this.http
      .delete<CommonResponse<VoidResponse[]>>(
        environment.apiUrl + "users/" + id
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  getUserTypes(): Observable<AccountUserType[]> {
    return this.http
      .get<CommonResponse<AccountUserType[]>>(
        environment.apiUrl + "users/types"
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  getUserData(): Observable<User> {
    return this.http
      .get<CommonResponse<User>>(environment.apiUrl + "user/data")
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  saveUserData(user: User): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(environment.apiUrl + "user/data", {
        name: user.name,
        last_name: user.last_name,
        email: user.email,
        phone_number: getFullPhoneNumber(user.phone_number_ui) ?? undefined,
        phone_number_country:
          getPhoneNumberCountry(user.phone_number_ui) ?? undefined,
      })
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  saveUserPhoneNumber(phoneNumber: PhoneNumberModel): Observable<User> {
    return this.http
      .post<CommonResponse<User>>(
        environment.apiUrl + "user/data/save-phone-number",
        {
          phone_number: getFullPhoneNumber(phoneNumber),
          phone_number_country: getPhoneNumberCountry(phoneNumber),
        }
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }

  savePreferences(data: UserPreferences): Observable<VoidResponse[]> {
    return this.http
      .post<CommonResponse<VoidResponse[]>>(
        environment.apiUrl + "user/preferences",
        {
          data: JSON.stringify(data),
        }
      )
      .pipe(
        map((resp) => processNormal(resp)),
        catchError(this.errorHandling.handleErrors)
      );
  }
}
